/**
 * 🌐 IPFS Yjs Provider - Store Yjs documents on Pinata IPFS
 * 
 * This provider stores the entire Yjs shared document on IPFS via Pinata,
 * creating a truly decentralized public archive that persists beyond
 * any single node or server.
 */

import { getYjsInstance } from './yjsLoader.js'

export class IPFSYjsProvider {
  constructor(documentId, ydoc, options = {}) {
    this.documentId = documentId
    this.ydoc = ydoc
    this.options = {
      autoSave: true,
      saveInterval: 30000, // Save every 30 seconds
      maxRetries: 3,
      pinataEndpoint: '/api/ipfs/upload',
      ...options
    }
    
    this.currentCID = null
    this.lastSaveTime = 0
    this.saveTimeout = null
    this.isConnected = false
    this.retryCount = 0
    
    console.log('🌐 IPFS Yjs Provider initialized:', {
      documentId: this.documentId,
      autoSave: this.options.autoSave,
      saveInterval: this.options.saveInterval
    })
    
    this.connect()
  }

  /**
   * Connect and start listening for document changes
   */
  async connect() {
    try {
      this.isConnected = true
      
      // Load existing document from IPFS if CID is stored
      await this.loadFromIPFS()
      
      // Set up auto-save on document changes
      if (this.options.autoSave) {
        this.ydoc.on('update', this.handleDocumentUpdate.bind(this))
        console.log('✅ IPFS Provider connected and listening for changes')
      }
      
      // Initial save to establish baseline
      await this.saveToIPFS()
      
    } catch (error) {
      console.error('❌ Failed to connect IPFS Provider:', error)
      this.isConnected = false
    }
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.isConnected = false
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    this.ydoc.off('update', this.handleDocumentUpdate.bind(this))
    console.log('🔌 IPFS Provider disconnected')
  }

  /**
   * Handle document updates with debounced saving
   */
  handleDocumentUpdate(update, origin) {
    if (!this.isConnected || origin === 'ipfs-load') {
      return
    }
    
    // Debounce saves to avoid excessive IPFS uploads
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    
    this.saveTimeout = setTimeout(() => {
      this.saveToIPFS()
    }, this.options.saveInterval)
    
    console.log('🔄 Document updated, scheduled IPFS save in', this.options.saveInterval, 'ms')
  }

  /**
   * Save the current Yjs document to IPFS via Pinata
   */
  async saveToIPFS() {
    if (!this.isConnected) {
      console.warn('⚠️ Not connected, skipping IPFS save')
      return
    }

    try {
      console.log('💾 Saving Yjs document to IPFS...')
      
      // Export the current document state
      const { Y } = await getYjsInstance()
      const documentState = Y.encodeStateAsUpdate(this.ydoc)
      
      // Create a blob from the document state
      const blob = new Blob([documentState], { 
        type: 'application/octet-stream' 
      })
      
      // Prepare metadata for Pinata
      const metadata = {
        documentId: this.documentId,
        timestamp: Date.now(),
        version: this.getDocumentVersion(),
        type: 'yjs-document',
        previousCID: this.currentCID
      }
      
      // Upload to Pinata via our upload endpoint
      const formData = new FormData()
      formData.append('file', blob, `${this.documentId}-${Date.now()}.yjs`)
      formData.append('metadata', JSON.stringify(metadata))
      
      const response = await fetch(this.options.pinataEndpoint, {
        method: 'POST',
        body: formData
      })
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      
      if (result.success) {
        const oldCID = this.currentCID
        this.currentCID = result.cid
        this.lastSaveTime = Date.now()
        this.retryCount = 0
        
        // Store CID in localStorage for persistence
        localStorage.setItem(`etherith-document-cid-${this.documentId}`, this.currentCID)
        
        console.log('✅ Document saved to IPFS:', {
          cid: this.currentCID,
          oldCID,
          pinned: result.proof?.pinned,
          size: blob.size,
          documentId: this.documentId
        })
        
        // Emit success event
        this.emit('ipfs-saved', {
          cid: this.currentCID,
          previousCID: oldCID,
          timestamp: this.lastSaveTime
        })
        
        return result
      } else {
        throw new Error(result.error || 'Upload failed')
      }
      
    } catch (error) {
      console.error('❌ Failed to save document to IPFS:', error)
      
      // Retry logic with exponential backoff
      if (this.retryCount < this.options.maxRetries) {
        this.retryCount++
        const retryDelay = Math.pow(2, this.retryCount) * 1000
        
        console.log(`🔄 Retrying IPFS save in ${retryDelay}ms (attempt ${this.retryCount}/${this.options.maxRetries})`)
        
        setTimeout(() => {
          this.saveToIPFS()
        }, retryDelay)
      } else {
        console.error('💥 Max retries exceeded for IPFS save')
        this.emit('ipfs-save-failed', error)
      }
      
      throw error
    }
  }

  /**
   * Load document from IPFS using stored CID
   */
  async loadFromIPFS() {
    try {
      // Get stored CID from localStorage
      const storedCID = localStorage.getItem(`etherith-document-cid-${this.documentId}`)
      
      if (!storedCID) {
        console.log('📭 No stored CID found for document, starting fresh')
        return
      }
      
      console.log('📥 Loading document from IPFS CID:', storedCID)
      
      // Try multiple IPFS gateways for reliability
      const gateways = [
        'https://gateway.pinata.cloud/ipfs/',
        'https://cloudflare-ipfs.com/ipfs/',
        'https://gateway.ipfs.io/ipfs/',
        'https://dweb.link/ipfs/'
      ]
      
      let documentData = null
      
      for (const gateway of gateways) {
        try {
          const response = await fetch(`${gateway}${storedCID}`, {
            timeout: 10000 // 10 second timeout
          })
          
          if (response.ok) {
            documentData = await response.arrayBuffer()
            console.log('✅ Document loaded from gateway:', gateway)
            break
          }
        } catch (gatewayError) {
          console.warn(`⚠️ Gateway ${gateway} failed:`, gatewayError.message)
          continue
        }
      }
      
      if (!documentData) {
        throw new Error('Failed to load from all IPFS gateways')
      }
      
      // Apply the loaded state to the document
      const { Y } = await getYjsInstance()
      const update = new Uint8Array(documentData)
      Y.applyUpdate(this.ydoc, update, 'ipfs-load')
      
      this.currentCID = storedCID
      console.log('✅ Document state restored from IPFS:', {
        cid: this.currentCID,
        size: documentData.byteLength,
        documentId: this.documentId
      })
      
      this.emit('ipfs-loaded', {
        cid: this.currentCID,
        size: documentData.byteLength
      })
      
    } catch (error) {
      console.error('❌ Failed to load document from IPFS:', error)
      // Don't throw - allow document to start fresh if IPFS load fails
    }
  }

  /**
   * Get the current document version (simple implementation)
   */
  getDocumentVersion() {
    return Math.floor(Date.now() / 1000) // Unix timestamp as version
  }

  /**
   * Get the current IPFS CID of the document
   */
  getCurrentCID() {
    return this.currentCID
  }

  /**
   * Force an immediate save to IPFS
   */
  async forceSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    return await this.saveToIPFS()
  }

  /**
   * Simple event emitter for provider events
   */
  emit(event, data) {
    const customEvent = new CustomEvent(`ipfs-yjs-${event}`, { 
      detail: { 
        provider: this, 
        documentId: this.documentId, 
        ...data 
      } 
    })
    window.dispatchEvent(customEvent)
  }

  /**
   * Get provider status and stats
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      currentCID: this.currentCID,
      lastSaveTime: this.lastSaveTime,
      documentId: this.documentId,
      autoSave: this.options.autoSave,
      retryCount: this.retryCount
    }
  }
}

/**
 * Create an IPFS provider for a Yjs document
 */
export function createIPFSProvider(documentId, ydoc, options = {}) {
  return new IPFSYjsProvider(documentId, ydoc, options)
}

/**
 * Helper to get all IPFS document CIDs stored locally
 */
export function getStoredDocumentCIDs() {
  const cids = {}
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('etherith-document-cid-')) {
      const documentId = key.replace('etherith-document-cid-', '')
      cids[documentId] = localStorage.getItem(key)
    }
  }
  return cids
}

/**
 * Helper to clear stored CID for a document
 */
export function clearStoredCID(documentId) {
  localStorage.removeItem(`etherith-document-cid-${documentId}`)
  console.log('🗑️ Cleared stored CID for document:', documentId)
}
